import Intercom from '@/components/intercom/intercom';
import { MeProvider, useMe } from '@/context/me/me.context';
import useAnalyticsPageViews from '@/hooks/use-analytics-view-page';
import { useHasActiveMembership } from '@/hooks/use-has-active-membership';
import { useGetFooterIsVisible } from '@/queries/layout.queries';
import { Flex } from '@cmpsr/components';
import React, { useEffect, useRef } from 'react';
import { Navigate, Outlet, ScrollRestoration, useLocation } from 'react-router-dom';
import FooterDashboard from '../dashboard/footer-dashboard/footer-dashboard';
import { ROUTE_SIGN_IN, ROUTE_ACCOUNT } from '@/constants/routes';
import authService from '@/services/auth.service';
import { getWorkosSessionEmail, hasWorkosSession } from '@/services/workos-auth-bridge';
import { getRoute, signInBounceUrl } from '@/utils';

function AuthGuard({ children }: { children: React.ReactNode }): React.ReactNode {
  const { data: me } = useMe();
  const location = useLocation();
  const { isActive, isLoading: isMembershipLoading } = useHasActiveMembership(me);
  const loggingOutRef = useRef(false);

  // Hard reload (not soft navigate) so AuthKit fully re-initializes against
  // the cleared cookie. A soft navigate to /sign-in would let
  // WorkosSignInRedirect re-register the WorkOS singleton from useAuth's
  // still-populated React state and bounce back to /dashboard.
  //
  // Orphan-WorkOS case (incident 2026-05-13): when a WorkOS session is
  // active but /me returns no member, the user authenticated through
  // AuthKit with an email that has no internal Catch account. We route
  // them to the no_account interstitial — a terminal state with "Sign up"
  // / "Try a different email" CTAs — instead of bare /sign-in, which
  // would re-fire SignIn's "user is null, kick to AuthKit" useEffect and
  // loop them straight back through AuthKit without ever showing UI.
  //
  // We do NOT await authService.logout() before setting location.href.
  // AuthKit's signOut follows a redirect chain that races us to /sign-in
  // and lands there *without* our ?reason=no_account query string,
  // restarting the loop. Setting location.href first wins the race; the
  // cookie clear still completes server-side, just on the new page load.
  useEffect(() => {
    if (me || loggingOutRef.current) return;
    loggingOutRef.current = true;
    const target = hasWorkosSession()
      ? signInBounceUrl('no_account', getWorkosSessionEmail())
      : getRoute(ROUTE_SIGN_IN);
    void authService.logout();
    window.location.href = target;
  }, [me]);

  if (!me) {
    return null;
  }

  // Membership is derived from catch-backend's subscription (source of truth),
  // falling back to /me's VIP flag when no Stripe subscription exists.
  if (!isMembershipLoading && !isActive && !location.pathname.startsWith(ROUTE_ACCOUNT)) {
    return <Navigate to={ROUTE_ACCOUNT} replace />;
  }

  return children;
}

export default function MainLayout(): React.ReactElement {
  const { data: isFooterVisible } = useGetFooterIsVisible();
  useAnalyticsPageViews();

  return (
    <Flex flexDirection="column" minHeight="100vh" background="background-page">
      <MeProvider>
        <AuthGuard>
          <Outlet />
          <Intercom />
          <ScrollRestoration />
        </AuthGuard>
      </MeProvider>
      {isFooterVisible && <FooterDashboard />}
    </Flex>
  );
}
